"use client";

import Skeleton from "@mui/material/Skeleton";
import { useWindowSize } from "hooks/use-window-size";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { links } from "@/data/links";
import { externalLinks } from "@/data/links/external-links";
import { useLang } from "@/hooks/use-lang";
import growinLogo from "@/public/home/growin-logo.svg";
import iphoneFrame from "@/public/home/iphone.webp";

import { Button } from "ui/core/button";
import { Accordion } from "ui/widget/accordion";

import { getAccordionData, videoUrlList } from "../data/robo";

export const RoboSection = () => {
  const { t, lang } = useLang();
  const dimensions = useWindowSize();
  const width = dimensions?.width ?? 0;
  const isDesktop = width >= 1024;
  const [activeIndex, setActiveIndex] = useState(0);
  const accordionData = getAccordionData(t);

  return (
    <section className="relative flex min-h-screen-dvh w-full items-center justify-center p-7 md:p-20">
      <div className="flex w-full max-w-[var(--home-page-width)] flex-col items-center justify-between gap-x-[10%] lg:flex-row">
        {/* video section */}
        {isDesktop ? <Video index={activeIndex} className="ml-[5%]" /> : null}
        {/* contents section */}
        <div className="w-full lg:max-w-[65%]">
          <header className="flex">
            <Image
              alt="growin-logo"
              src={growinLogo as StaticImageData}
              width={47}
              height={47}
              className="mr-4 sm:h-[75px] sm:w-[75px]"
            />
            <div className="flex flex-col justify-around">
              <h2 className="text-xl font-bold lg:text-2xl 2xl:text-3xl">
                {t.home.roboSection.title}
              </h2>
              <h3>{t.home.roboSection.subtitle}</h3>
            </div>
          </header>
          {/* accordion */}
          <Accordion
            data={accordionData.map((data, i) => ({
              title: (
                <h4 className="py-2 font-bold 2xl:text-xl">{data.title}</h4>
              ),
              content: (
                <div className="flex flex-col items-center justify-center">
                  <p>{data.content}</p>
                  {!isDesktop && activeIndex === i && (
                    <Video index={i} className="mt-5" />
                  )}
                </div>
              ),
            }))}
            mustShown
            showTopLine
            showBottomLine
            showActiveLine
            iconAnchor="right"
            onChange={(index: number | null) => setActiveIndex(index ?? 0)}
            containerClassName="mt-12"
            activeLineColor="linear-gradient(90deg, rgba(20,179,188,1) 0%, rgba(142,220,153,1) 100%)"
          />
          {/* links */}
          <div className="mt-7 flex flex-wrap items-center gap-6 lg:gap-12">
            <div className="mt-0.5 flex items-center gap-4">
              <Link
                href={externalLinks.growinGooglePlay}
                target="_blank"
                aria-label="go to google play page"
              >
                <div className="h-16 w-16 bg-[url('/home/googleplay.svg')] bg-center bg-no-repeat transition-all hover:bg-[url('/home/download.svg')]" />
              </Link>
              <Link
                href={externalLinks.growinAppStore}
                target="_blank"
                aria-label="go to app store page"
              >
                <div className="h-16 w-16 bg-[url('/home/ios.svg')] bg-center bg-no-repeat transition-all hover:bg-[url('/home/download.svg')]" />
              </Link>
            </div>
            <Link
              href={`/${lang}${links.roboAdvisor}`}
              aria-label="learn more about robo advisor"
            >
              <Button
                variant="secondary"
                className="px-10 py-4 text-[#2ABA77] ring-[#2ABA77] transition-colors hover:bg-[#2ABA77] hover:text-white sm:text-base"
              >
                {t.home.readMore}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const Video = ({ index, className }: { index: number; className?: string }) => {
  const { lang } = useLang();
  const videoUrl = videoUrlList[index][lang];
  const [loading, setLoading] = useState(true);

  return (
    <div className={twMerge("relative h-fit w-fit shrink-0", className)}>
      {loading && <Loader />}
      <video
        id={`video-${index}-${lang}`}
        key={`video-${index}-${lang}`}
        autoPlay
        loop
        muted
        preload="auto"
        playsInline
        onLoadStart={() => setLoading(true)}
        onLoadedData={() => setLoading(false)}
        className="absolute left-[50%] top-0 h-full w-full -translate-x-[50%] animate-fadeIn rounded-[40px] object-contain p-2 2xl:rounded-[60px]"
        data-testid={`video ${index}`}
      >
        <source src={videoUrl.mp4} type="video/mp4" />
        <source src={videoUrl.ogg} type="video/ogg" />
      </video>
      <Image
        priority
        alt="iphone-frame"
        src={iphoneFrame}
        width={181}
        height={362}
        style={{ width: "auto", height: "auto" }}
        className="unselectable aspect-auto relative lg:!w-[250px] 2xl:!w-[350px]"
      />
    </div>
  );
};

const Loader = () => (
  <div className="absolute left-[50%] top-[50%] flex h-[80%] w-[60%] -translate-x-[50%] -translate-y-[50%] flex-col items-center justify-center gap-2">
    <Skeleton variant="rectangular" width="100%" height="30%" />
    <Skeleton variant="text" width="100%" />
    <Skeleton variant="text" width="100%" />
    <Skeleton variant="rectangular" width="100%" height="20%" />
    <Skeleton variant="text" width="100%" />
    <Skeleton variant="text" width="100%" />
    <Skeleton variant="rectangular" width="100%" height="20%" />
  </div>
);
